import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import { api } from '../lib/api-client';
import type { ActivePlan, BillingInterval, Plan } from '@flymind/shared';
import { PLAN_LIMITS, PLAN_PRICES, PLAN_DISPLAY_NAMES } from '@flymind/shared';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';

// --- Plan display config ---
interface PlanDisplay {
  id: ActivePlan;
  name: string;
  priceMonthly: string;
  priceYearly: string;
  features: string[];
  highlighted?: boolean;
}

function getFaqItems(t: (k: any) => string): { q: string; a: string }[] {
  return [
    { q: t('subscription.faqQ1'), a: t('subscription.faqA1') },
    { q: t('subscription.faqQ2'), a: t('subscription.faqA2') },
    { q: t('subscription.faqQ3'), a: t('subscription.faqA3') },
    { q: t('subscription.faqQ4'), a: t('subscription.faqA4') },
  ];
}

function getPlans(t: (k: any) => string, locale: string): PlanDisplay[] {
  const decSep = locale === 'en' ? '.' : ',';
  const fmtPrice = (n: number) => `€${n.toFixed(2).replace('.', decSep)}`;
  return [
    {
      id: 'free',
      name: t('subscription.free'),
      priceMonthly: t('subscription.priceFree'),
      priceYearly: t('subscription.priceFree'),
      features: [
        t('subscription.sentinelOne'),
        t('subscription.notifEmailPush'),
        t('subscription.historyPrices'),
      ],
    },
    {
      id: 'premium',
      name: t('subscription.pro'),
      priceMonthly: `${fmtPrice(PLAN_PRICES.premium.monthly)}/${t('subscription.perMonth')}`,
      priceYearly: `${fmtPrice(PLAN_PRICES.premium.yearly)}/${t('subscription.perYear')}`,
      features: [
        t('subscription.sentinelFive'),
        t('subscription.notifEmailPush'),
        t('subscription.historyPrices'),
        t('subscription.predictiveAlerts'),
        t('subscription.aiCompile'),
      ],
    },
    {
      id: 'unlimited',
      name: 'Unlimited',
      priceMonthly: `${fmtPrice(PLAN_PRICES.unlimited.monthly)}/${t('subscription.perMonth')}`,
      priceYearly: `${fmtPrice(PLAN_PRICES.unlimited.yearly)}/${t('subscription.perYear')}`,
      highlighted: true,
      features: [
        t('subscription.sentinelUnlimited'),
        t('subscription.notifEmailPush'),
        t('subscription.historyPrices'),
        t('subscription.predictiveAlerts'),
        t('subscription.aiCompile'),
        t('subscription.voiceAgentFeature'),
      ],
    },
  ];
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between py-4 text-left text-sm font-medium text-text-primary cursor-pointer hover:text-primary-600 transition-colors"
      >
        <span>{q}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className={`h-5 w-5 shrink-0 text-text-secondary transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        >
          <path
            fillRule="evenodd"
            d="M5.22 8.22a.75.75 0 011.06 0L10 11.94l3.72-3.72a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.22 9.28a.75.75 0 010-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {open && (
        <div className="pb-4 text-sm text-text-secondary">{a}</div>
      )}
    </div>
  );
}

// --- Billing interval toggle ---
function IntervalToggle({
  interval,
  onChange,
}: {
  interval: BillingInterval;
  onChange: (i: BillingInterval) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center gap-3 mb-6">
      <span
        className={`text-sm font-medium ${interval === 'monthly' ? 'text-text-primary' : 'text-text-secondary'}`}
      >
        {t('subscription.monthly')}
      </span>
      <button
        type="button"
        onClick={() => onChange(interval === 'monthly' ? 'yearly' : 'monthly')}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
          interval === 'yearly' ? 'bg-primary-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            interval === 'yearly' ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
      <span
        className={`text-sm font-medium ${interval === 'yearly' ? 'text-text-primary' : 'text-text-secondary'}`}
      >
        {t('subscription.yearly')}
      </span>
      {interval === 'yearly' && (
        <Badge variant="good" size="sm">
          -16%
        </Badge>
      )}
    </div>
  );
}

// --- Plan Card ---
function PlanCard({
  plan,
  currentPlan,
  interval,
  onSelect,
  loading,
}: {
  plan: PlanDisplay;
  currentPlan: ActivePlan;
  interval: BillingInterval;
  onSelect: (planId: ActivePlan, interval: BillingInterval) => void;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const isCurrent = plan.id === currentPlan;
  const isUpgrade = plan.id === 'premium' && currentPlan === 'free';
  const isDowngrade = plan.id === 'free' && currentPlan === 'premium';
  const price = interval === 'monthly' ? plan.priceMonthly : plan.priceYearly;

  return (
    <Card
      variant={plan.highlighted ? 'bordered' : 'default'}
      padding="md"
      className={`flex flex-col ${isCurrent ? 'ring-2 ring-primary-500' : ''}`}
    >
      {plan.highlighted && (
        <Badge variant="info" size="sm" className="self-start mb-3">
          {t('subscription.recommended')}
        </Badge>
      )}

      <h3 className="text-lg font-semibold text-text-primary">{plan.name}</h3>
      <p className="text-2xl font-bold text-text-primary mt-1">{price}</p>

      <ul className="mt-4 mb-6 space-y-2 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-text-secondary">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-4 w-4 shrink-0 text-primary-600 mt-0.5"
            >
              <path
                fillRule="evenodd"
                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                clipRule="evenodd"
              />
            </svg>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {isCurrent ? (
        <Button variant="secondary" disabled className="w-full">
          {t('subscription.currentPlan')}
        </Button>
      ) : isUpgrade ? (
        <Button
          variant="primary"
          className="w-full"
          onClick={() => onSelect(plan.id, interval)}
          isLoading={loading}
        >
          {t('subscription.upgradeBuy')} {plan.name}
        </Button>
      ) : isDowngrade ? (
        <Button
          variant="outline"
          className="w-full"
          onClick={() => onSelect(plan.id, interval)}
          isLoading={loading}
        >
          {t('subscription.downgradeShort')}
        </Button>
      ) : null}
    </Card>
  );
}

// --- Main Subscription page ---
export default function Subscription() {
  const { user } = useAuth();
  const { t, locale } = useTranslation();
  const rawPlan = (user?.plan || 'free') as Plan;
  const currentPlan: ActivePlan = rawPlan as ActivePlan;
  const [interval, setInterval] = useState<BillingInterval>('yearly');
  const [loadingPlan, setLoadingPlan] = useState<ActivePlan | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState('');

  const plans = getPlans(t, locale);
  const faqItems = getFaqItems(t);

  const handleSelectPlan = async (plan: ActivePlan, billingInterval: BillingInterval) => {
    if (plan === 'free') {
      // Downgrade to free goes through Stripe portal
      handleManageSubscription();
      return;
    }

    setLoadingPlan(plan);
    setError('');
    try {
      const result = await api.post<{ url: string }>('/subscriptions/checkout', {
        plan,
        interval: billingInterval,
      });
      window.location.href = result.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : t('subscription.checkoutError'));
      setLoadingPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    setError('');
    try {
      const result = await api.post<{ url: string }>('/subscriptions/portal');
      window.location.href = result.url;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('subscription.portalError'),
      );
      setPortalLoading(false);
    }
  };

  const planLabel = PLAN_DISPLAY_NAMES[rawPlan] || rawPlan;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
      <h1 className="text-xl font-bold text-text-primary mb-6">
        {t('subscription.pageTitle')}
      </h1>

      {/* Current plan status */}
      <Card variant="default" padding="md" className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="text-sm text-text-secondary">{t('subscription.currentPlan')}</div>
            <div className="text-lg font-semibold text-text-primary mt-0.5 flex items-center gap-2">
              {planLabel}
              {currentPlan !== 'free' && (
                <Badge variant="good" size="sm">
                  {t('common.active')}
                </Badge>
              )}
            </div>
            <div className="text-xs text-text-secondary mt-1">
              {PLAN_LIMITS[rawPlan].maxMonitoraggi >= 999
                ? t('subscription.unlimitedSentinels')
                : `${t('subscription.sentinelMaxPrefix')} ${PLAN_LIMITS[rawPlan].maxMonitoraggi} ${t('subscription.sentinelSuffix')}`}
              {PLAN_LIMITS[rawPlan].aiAnalysis && ` · ${t('subscription.aiBadge')}`}
              {PLAN_LIMITS[rawPlan].voiceAssistant && ` · ${t('subscription.voiceBadge')}`}
            </div>
          </div>

          {currentPlan !== 'free' && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleManageSubscription}
              isLoading={portalLoading}
            >
              {t('subscription.manageSubscription')}
            </Button>
          )}
        </div>
      </Card>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-alert-exceptional">
          {error}
        </div>
      )}

      {/* Plan comparison */}
      <h2 className="text-lg font-semibold text-text-primary mb-4">
        {t('subscription.changePlan')}
      </h2>

      <IntervalToggle interval={interval} onChange={setInterval} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            currentPlan={currentPlan}
            interval={interval}
            onSelect={handleSelectPlan}
            loading={loadingPlan === plan.id}
          />
        ))}
      </div>

      {/* FAQ */}
      <Card variant="default" padding="lg">
        <h2 className="text-lg font-semibold text-text-primary mb-2">
          {t('landing.faq.title')}
        </h2>
        <div>
          {faqItems.map((item) => (
            <FaqItem key={item.q} q={item.q} a={item.a} />
          ))}
        </div>
      </Card>
    </div>
  );
}
